import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutDashboard, ShoppingBag, ChefHat, CreditCard, Menu, Users, QrCode, MapPin, TrendingUp, Clock, DollarSign, Plus } from "lucide-react";
import { useBackgroundSync } from "../hooks/useBackgroundSync";
import { useDemoV2 } from "../hooks/useDemoV2";
import type { DemoOrderItem } from "@/lib/kpis/demoKpis";

type LegacyOrderItem = DemoOrderItem & {
  item_id: string;
  price?: number;
  notes?: string;
};

type SelectedOrderItem = LegacyOrderItem & {
  price: number;
  quantity: number;
  id?: string;
  name?: string;
  name_snapshot?: string;
  price_snapshot_cents?: number;
  price_cents?: number;
};

type LegacyDemoOrder = {
  id: string;
  order_code?: string;
  table_number?: string;
  customer_name?: string;
  customer_phone?: string;
  items?: LegacyOrderItem[];
  total_amount?: number;
  payment_method?: string;
  status?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
};

type LegacyMenuItem = {
  id: string;
  name: string;
  description?: string;
  price: number;
  category: string;
  is_available: boolean;
  image_url?: string | null;
  created_at?: string;
};

type AvailableTable = {
  id: string;
  name: string;
  description: string;
};

const V1DemoDashboard = () => {
  console.log('🎯 V1DemoDashboard rendering...');
  
  // Demo mode detection and navigation
  const navigate = useNavigate();
  const location = useLocation();
  const isDemoQuery = new URLSearchParams(location.search).get('mode') === 'demo';
  const demoV2 = useDemoV2();
  const isDemoEffective = isDemoQuery || demoV2?.isDemo;
  
  // KPI values from demo hook
  const { todayOrdersCount, revenueCents, kitchenQueue, orderCount, orders: hookOrders } = useDemoV2();
  
  // Dev-only breadcrumb for KPIs
  if (import.meta.env.DEV) {
    console.debug('[V1Dashboard KPIs]', {
      todayOrdersCount,
      revenueCents,
      kitchenQueue,
      orderCount,
      ordersLen: hookOrders?.length ?? 0,
    });
  }
  
  // Simple state management
  const [orders, setOrders] = useState<LegacyDemoOrder[]>([]);
  const [restaurantName, setRestaurantName] = useState<string>('Demo Restaurant');
  const [activeSubdomain, setActiveSubdomain] = useState<string>('demo');
  
  // Center peek modal state
  const [isOrderModalOpen, setIsOrderModalOpen] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<LegacyMenuItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectedOrderItem[]>([]);
  const [orderTotal, setOrderTotal] = useState<number>(0);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  // Advanced order features state
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [orderNotes, setOrderNotes] = useState<string>('');
  const [itemModifications, setItemModifications] = useState<{[key: string]: string}>({});
  
  // Animation and focus state
  const [modifyingItem, setModifyingItem] = useState<LegacyMenuItem | null>(null);
  const [isModifying, setIsModifying] = useState<boolean>(false);
  
  // Checkout state
  const [isCheckoutOpen, setIsCheckoutOpen] = useState<boolean>(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [selectedTable, setSelectedTable] = useState<string>('');
  
  // Use background sync for placing orders only (KPIs now come from demo hook)
  const { placeOrderToSupabase } = useBackgroundSync();

  // Mount breadcrumb to show which path will be taken
  useEffect(() => {
    console.debug('[V1DemoDashboard] mount', { isDemoQuery, isDemoHook: !!demoV2?.isDemo, isDemoEffective });
  }, [isDemoQuery, demoV2?.isDemo, isDemoEffective]);

  const getModificationPrice = useCallback((itemId: string, quantity: number = 1) => {
    if (!itemModifications[itemId]) return 0;
    const modificationCount = itemModifications[itemId].split(',').length;
    return modificationCount * 0.75 * quantity;
  }, [itemModifications]);

  const getItemTotalPrice = useCallback((item: SelectedOrderItem) => {
    const basePrice = (item.price ?? 0) * item.quantity;
    const modificationPrice = getModificationPrice(item.item_id, item.quantity);
    return basePrice + modificationPrice;
  }, [getModificationPrice]);

  const calculateOrderTotal = useCallback(() => {
    console.log('📊 V1 Dashboard: Calculating order total...');
    console.log('📊 V1 Dashboard: Selected items:', selectedItems);
    console.log('📊 V1 Dashboard: Item modifications:', itemModifications);

    const total = selectedItems.reduce((sum, item) => {
      const itemTotal = getItemTotalPrice(item);
      console.log(`📊 V1 Dashboard: Item ${item.item_name ?? item.name ?? 'Item'} total: $${itemTotal}`);
      return sum + itemTotal;
    }, 0);

    const roundedTotal = Math.round(total * 100) / 100;
    console.log('📊 V1 Dashboard: Calculated total:', total, 'Rounded:', roundedTotal);
    setOrderTotal(roundedTotal);
  }, [getItemTotalPrice, itemModifications, selectedItems]);

  // Recalculate total whenever selectedItems or itemModifications change
  useEffect(() => {
    calculateOrderTotal();
  }, [calculateOrderTotal]);

  // Initialize demo data if not exists
  const initializeDemoData = useCallback(() => {
    try {
      // Check if demo data already exists
      const existingOrders = localStorage.getItem('braise:demo_orders');
      const existingMenuItems = localStorage.getItem('braise:demo_menu_items');
      
      if (!existingOrders) {
        // Create sample orders
        const sampleOrders: LegacyDemoOrder[] = [
          {
            id: 'order_1',
            order_code: 'ORD-001',
            table_number: 'Table 5',
            customer_name: 'John Doe',
            customer_phone: '555-0123',
            items: [
              { item_id: 'item_1', item_name: 'Margherita Pizza', quantity: 1, price: 15.99 },
              { item_id: 'item_2', item_name: 'Caesar Salad', quantity: 1, price: 8.99 }
            ],
            total_amount: 24.98,
            payment_method: 'card',
            status: 'PENDING_PAYMENT',
            notes: 'Extra cheese on pizza',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 'order_2',
            order_code: 'ORD-002',
            table_number: 'Table 3',
            customer_name: 'Jane Smith',
            customer_phone: '555-0456',
            items: [
              { item_id: 'item_3', item_name: 'Chicken Burger', quantity: 2, price: 12.99 }
            ],
            total_amount: 25.98,
            payment_method: 'cash',
            status: 'IN_PROGRESS',
            notes: 'No pickles',
            created_at: new Date(Date.now() - 10 * 60000).toISOString(), // 10 minutes ago
            updated_at: new Date().toISOString()
          },
          {
            id: 'order_3',
            order_code: 'ORD-003',
            table_number: 'Counter',
            customer_name: 'Mike Johnson',
            customer_phone: '555-0789',
            items: [
              { item_id: 'item_4', item_name: 'Pasta Carbonara', quantity: 1, price: 16.99 },
              { item_id: 'item_5', item_name: 'Garlic Bread', quantity: 1, price: 4.99 }
            ],
            total_amount: 21.98,
            payment_method: 'card',
            status: 'READY',
            notes: 'Takeout order',
            created_at: new Date(Date.now() - 20 * 60000).toISOString(), // 20 minutes ago
            updated_at: new Date().toISOString()
          }
        ];
        
        localStorage.setItem('braise:demo_orders', JSON.stringify(sampleOrders));
        console.log('📊 V1 Dashboard: Created sample orders:', sampleOrders.length);
      }
      
      if (!existingMenuItems) {
        // Create sample menu items
        const sampleMenuItems: LegacyMenuItem[] = [
          {
            id: 'item_1',
            name: 'Margherita Pizza',
            description: 'Classic pizza with tomato sauce, mozzarella, and fresh basil',
            price: 15.99,
            category: 'Pizza',
            is_available: true,
            image_url: null,
            created_at: new Date().toISOString()
          },
          {
            id: 'item_2',
            name: 'Caesar Salad',
            description: 'Fresh romaine lettuce with caesar dressing, croutons, and parmesan',
            price: 8.99,
            category: 'Salads',
            is_available: true,
            image_url: null,
            created_at: new Date().toISOString()
          },
          {
            id: 'item_3',
            name: 'Chicken Burger',
            description: 'Grilled chicken breast with lettuce, tomato, and mayo',
            price: 12.99,
            category: 'Burgers',
            is_available: true,
            image_url: null,
            created_at: new Date().toISOString()
          },
          {
            id: 'item_4',
            name: 'Pasta Carbonara',
            description: 'Creamy pasta with bacon, eggs, and parmesan cheese',
            price: 16.99,
            category: 'Pasta',
            is_available: true,
            image_url: null,
            created_at: new Date().toISOString()
          },
          {
            id: 'item_5',
            name: 'Garlic Bread',
            description: 'Toasted bread with garlic butter and herbs',
            price: 4.99,
            category: 'Sides',
            is_available: true,
            image_url: null,
            created_at: new Date().toISOString()
          },
          {
            id: 'item_6',
            name: 'Chocolate Cake',
            description: 'Rich chocolate cake with chocolate frosting',
            price: 6.99,
            category: 'Desserts',
            is_available: false, // This one is unavailable
            image_url: null,
            created_at: new Date().toISOString()
          }
        ];
        
        localStorage.setItem('braise:demo_menu_items', JSON.stringify(sampleMenuItems));
        console.log('📊 V1 Dashboard: Created sample menu items:', sampleMenuItems.length);
      }
    } catch (error) {
      console.error('Error initializing demo data:', error);
    }
  }, []);

  // Load data from localStorage
  useEffect(() => {
    try {
      // Initialize demo data first
      initializeDemoData();
      
      // Load demo info
      const slug = localStorage.getItem('braise:demo_slug_v2');
      const name = localStorage.getItem('braise:demo_restaurant_name_v2');
      
      if (slug) setActiveSubdomain(slug);
      if (name) setRestaurantName(name);

      // Load orders
      const storedOrders = localStorage.getItem('braise:demo_orders');
      if (storedOrders) {
        const parsedOrders = JSON.parse(storedOrders) as LegacyDemoOrder[];
        if (Array.isArray(parsedOrders)) {
          setOrders(parsedOrders);
          console.log('📊 V1 Dashboard: Loaded orders from localStorage:', parsedOrders.length);
        }
      }

      // Load menu items for order modal
      const storedMenuItems = localStorage.getItem('braise:demo_menu_items');
      if (storedMenuItems) {
        const parsedMenuItems = JSON.parse(storedMenuItems) as LegacyMenuItem[];
        if (Array.isArray(parsedMenuItems)) {
          setMenuItems(parsedMenuItems);
          console.log('📊 V1 Dashboard: Loaded menu items from localStorage:', parsedMenuItems.length);
        }
      }
    } catch (error) {
      console.error('Error loading data from localStorage:', error);
    }
  }, [initializeDemoData]);

  // KPIs now come from demo hook, no need for background sync event listeners

  // KPIs now come directly from demo hook

  // Defensive navigation function
  const handleNavigate = (path: string) => {
    try {
      window.location.href = `/r/${activeSubdomain}${path}?mode=demo`;
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  // New Counter Order handler - opens center peek modal
  const handleNewCounterOrder = () => {
    console.log('📊 V1 Dashboard: Opening New Counter Order modal');
    setIsOrderModalOpen(true);
    setSelectedItems([]);
    setOrderTotal(0);
  };

  // Add item to order
  const addItemToOrder = (item: LegacyMenuItem) => {
    setSelectedItems(prevItems => {
      const existingItem = prevItems.find(selected => selected.item_id === item.id);
      if (existingItem) {
        return prevItems.map(selected =>
          selected.item_id === item.id
            ? { ...selected, quantity: selected.quantity + 1 }
            : selected
        );
      }

      const newItem: SelectedOrderItem = {
        item_id: item.id,
        item_name: item.name,
        quantity: 1,
        price: item.price,
        notes: '',
        id: item.id,
        name: item.name,
        name_snapshot: item.name,
        price_snapshot_cents: Math.round(item.price * 100),
        price_cents: Math.round(item.price * 100),
      };
      return [...prevItems, newItem];
    });
  };

  // Remove item from order
  const removeItemFromOrder = (itemId: string) => {
    setSelectedItems(prevItems => prevItems.filter(item => item.item_id !== itemId));
  };

  // Update item quantity
  const updateItemQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItemFromOrder(itemId);
      return;
    }
    setSelectedItems(prevItems =>
      prevItems.map(item =>
        item.item_id === itemId ? { ...item, quantity } : item
      )
    );
  };

  // Open checkout modal
  const openCheckout = () => {
    if (selectedItems.length === 0) {
      console.log('📊 V1 Dashboard: No items selected for checkout');
      return;
    }
    setIsCheckoutOpen(true);
  };

  // Process payment and place order
  const processPayment = async () => {
    if (!selectedPaymentMethod) {
      console.log('📊 V1 Dashboard: No payment method selected');
      return;
    }

    if (!selectedTable) {
      console.log('📊 V1 Dashboard: No table selected');
      return;
    }

    if (isDemoEffective) {
      console.debug('[V1DemoDashboard] DEMO modal submit start');

      // Build tolerant item draft from the modal's state
      const itemsDraft = selectedItems.map((item) => {
        const name = item.item_name ?? item.name ?? item.name_snapshot ?? 'Item';
        const price_cents = item.price_snapshot_cents ?? item.price_cents ?? Math.round((item.price ?? 0) * 100);
        const qty = Number(item.quantity ?? 1);
        const notes = itemModifications[item.item_id] || item.notes || undefined;
        
        return {
          id: item.id ?? item.item_id,
          name,
          price_cents,
          qty,
          notes,
          modifiers: []
        };
      });

      const tableNumber = String(selectedTable || '1');

      console.debug('[V1DemoDashboard] DEMO draft', {
        tableNumber,
        items: itemsDraft.slice(0, 3),
        count: itemsDraft.length,
      });

      // Call the hook (optimistic append happens inside createCounterOrder)
      await demoV2.createCounterOrder({
        tableNumber,
        items: itemsDraft,
        guests: Number(1),
        notes: orderNotes || `Order for table ${tableNumber} from dashboard`
      });

      console.debug('[V1DemoDashboard] DEMO created; optimistic append done');

      // Close all modals and reset
      setIsCheckoutOpen(false);
      setIsOrderModalOpen(false);
      setSelectedItems([]);
      setOrderTotal(0);
      setSelectedPaymentMethod('');
      
      // Navigate to Orders (preserve demo mode)
      const slug = demoV2?.slug || 'demo';
      navigate(`/r/${slug}/orders?mode=demo`);
      return;
    }

    // REAL (non-demo) path: keep existing logic unchanged
    const selectedTableData = getAvailableTables().find(table => table.id === selectedTable);
    const tableName = selectedTableData ? selectedTableData.name : 'Unknown Table';

    const order = {
      table_number: tableName,
      customer_name: customerName || 'Walk-in Customer',
      customer_phone: customerPhone || '',
      items: selectedItems.map(item => ({
        ...item,
        notes: itemModifications[item.item_id] || item.notes || ''
      })),
      total_amount: orderTotal,
      payment_method: selectedPaymentMethod,
      notes: orderNotes || `Order for ${tableName} from dashboard`
    };
    
    try {
      console.log('📊 V1 Dashboard: Processing payment and placing order:', order);
      await placeOrderToSupabase(order);
      console.log('📊 V1 Dashboard: Order placed successfully with payment:', selectedPaymentMethod);
      
      // Close all modals and reset
      setIsCheckoutOpen(false);
      setIsOrderModalOpen(false);
      setSelectedItems([]);
      setOrderTotal(0);
      setSelectedPaymentMethod('');
      
      // KPIs now come from demo hook, no need to recalculate
    } catch (error) {
      console.error('📊 V1 Dashboard: Error processing payment and placing order:', error);
    }
  };

  // Get available tables
  const getAvailableTables = (): AvailableTable[] => {
    return [
      { id: 'counter', name: 'Counter', description: 'Walk-in orders' },
      { id: 'table-1', name: 'Table 1', description: '2-seater by window' },
      { id: 'table-2', name: 'Table 2', description: '4-seater booth' },
      { id: 'table-3', name: 'Table 3', description: '2-seater corner' },
      { id: 'table-4', name: 'Table 4', description: '6-seater family' },
      { id: 'table-5', name: 'Table 5', description: '4-seater center' },
      { id: 'table-6', name: 'Table 6', description: '2-seater bar' },
      { id: 'table-7', name: 'Table 7', description: '4-seater patio' },
      { id: 'table-8', name: 'Table 8', description: '8-seater party' },
      { id: 'takeout', name: 'Takeout', description: 'Pickup orders' },
      { id: 'delivery', name: 'Delivery', description: 'Home delivery' }
    ];
  };

  // Close checkout modal
  const closeCheckout = () => {
    setIsCheckoutOpen(false);
    setSelectedPaymentMethod('');
    setSelectedTable('');
  };

  // Close order modal
  const closeOrderModal = () => {
    setIsOrderModalOpen(false);
    setSelectedItems([]);
    setOrderTotal(0);
    setSearchQuery('');
    setSelectedCategory('all');
    setCustomerName('');
    setCustomerPhone('');
    setOrderNotes('');
    setSelectedPaymentMethod('cash');
    setItemModifications({});
    setModifyingItem(null);
    setIsModifying(false);
    setIsCheckoutOpen(false);
    setSelectedTable('');
  };

  // Get filtered menu items based on search and category
  const getFilteredMenuItems = (): LegacyMenuItem[] => {
    let filtered = menuItems.filter(item => item.is_available !== false);

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(query) ||
        (item.description ?? '').toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  // Get available categories
  const getAvailableCategories = (): string[] => {
    return ['all', ...Array.from(new Set(menuItems.map(item => item.category ?? 'Other')))];
  };

  // Get popular items for quick actions
  const getPopularItems = (): LegacyMenuItem[] => {
    return menuItems.filter(item => item.is_available !== false).slice(0, 6);
  };

  // Get recent orders for quick reordering
  const getRecentOrders = (): LegacyDemoOrder[] => {
    return orders.slice(0, 3);
  };

  // Add item modification
  const addItemModification = (itemId: string, modification: string) => {
    setItemModifications(prev => ({
      ...prev,
      [itemId]: prev[itemId] ? `${prev[itemId]}, ${modification}` : modification
    }));
  };

  // Remove item modification
  const removeItemModification = (itemId: string) => {
    setItemModifications(prev => {
      const newMods = { ...prev };
      delete newMods[itemId];
      return newMods;
    });
  };

  // Quick reorder from recent orders
  const quickReorder = (order: LegacyDemoOrder) => {
    const normalizedItems: SelectedOrderItem[] = (order.items ?? []).map(item => ({
      item_id: item.item_id,
      item_name: item.item_name ?? item.name ?? 'Item',
      quantity: item.quantity ?? 1,
      price: item.price ?? 0,
      notes: item.notes,
      id: item.item_id,
      name: item.name,
      name_snapshot: item.name_snapshot,
      price_snapshot_cents: item.price_snapshot_cents,
      price_cents: item.price_cents,
    }));
    setSelectedItems(normalizedItems);
    setCustomerName(order.customer_name ?? '');
    setCustomerPhone(order.customer_phone ?? '');
  };

  // Start animated modification
  const startModification = (item: LegacyMenuItem) => {
    setModifyingItem(item);
    setIsModifying(true);
  };

  // Close animated modification
  const closeModification = () => {
    setIsModifying(false);
    setModifyingItem(null);
  };

  // Get available toppings based on item category
  const getAvailableToppings = (item: LegacyMenuItem): string[] => {
    const baseToppings = [
      'Extra Cheese', 'No Pickles', 'Extra Spicy', 'Well Done', 'No Onions', 'Extra Sauce'
    ];
    
    const categoryToppings: {[key: string]: string[]} = {
      'Pizza': ['Extra Cheese', 'Extra Sauce', 'No Onions', 'Extra Spicy', 'Well Done', 'Extra Basil'],
      'Burgers': ['Extra Cheese', 'No Pickles', 'No Onions', 'Extra Sauce', 'Well Done', 'Extra Lettuce'],
      'Salads': ['Extra Dressing', 'No Onions', 'Extra Cheese', 'Extra Croutons', 'No Tomatoes', 'Extra Avocado'],
      'Pasta': ['Extra Cheese', 'Extra Sauce', 'Extra Spicy', 'Well Done', 'No Onions', 'Extra Basil'],
      'Sides': ['Extra Sauce', 'Extra Spicy', 'Well Done', 'Extra Salt', 'No Salt', 'Extra Butter'],
      'Desserts': ['Extra Whipped Cream', 'Extra Chocolate', 'Extra Vanilla', 'No Nuts', 'Extra Syrup', 'Extra Sprinkles']
    };

    const categoryKey = item.category ?? '';
    return categoryToppings[categoryKey] || baseToppings;
  };

  // V1 Dashboard Stats (using demo hook values)
  const dashboardStats = [{
    title: "Today's Orders",
    value: todayOrdersCount.toString(),
    change: "+12%",
    icon: ShoppingBag,
    color: "text-primary"
  }, {
    title: "Revenue",
    value: `$${(revenueCents / 100).toFixed(2)}`,
    change: "+8%",
    icon: DollarSign,
    color: "text-success"
  }, {
    title: "Active Tables",
    value: "8/12",
    change: "67%",
    icon: MapPin,
    color: "text-warning"
  }, {
    title: "Kitchen Queue",
    value: kitchenQueue.toString(),
    change: "Normal",
    icon: ChefHat,
    color: "text-secondary"
  }];

  // V1 Quick Actions (exact same structure as original)
  const quickActions = [{
    title: "Orders",
    description: "View and manage all orders",
    icon: ShoppingBag,
    path: "/orders",
    color: "bg-primary/10 text-primary"
  }, {
    title: "Kitchen",
    description: "Live kitchen order queue",
    icon: ChefHat,
    path: "/kitchen",
    color: "bg-secondary/10 text-secondary"
  }, {
    title: "Menu",
    description: "Manage your menu items",
    icon: Menu,
    path: "/menu",
    color: "bg-accent/10 text-accent-foreground"
  }, {
    title: "QR Codes",
    description: "Generate table QR codes",
    icon: QrCode,
    path: "/qr-codes",
    color: "bg-warning/10 text-warning-foreground"
  }, {
    title: "Payments",
    description: "View payment history",
    icon: CreditCard,
    path: "/payments",
    color: "bg-success/10 text-success"
  }, {
    title: "Staff",
    description: "Manage team members",
    icon: Users,
    path: "/staff",
    color: "bg-muted text-muted-foreground"
  }];

  // V1 Recent Orders (exact same structure as original)
  const recentOrders = [{
    id: "#1234",
    table: "Table 5",
    items: "2x Margherita Pizza",
    total: "$24.99",
    status: "preparing",
    time: "5 min ago"
  }, {
    id: "#1235",
    table: "Table 2",
    items: "1x Caesar Salad, 1x Pasta",
    total: "$18.50",
    status: "ready",
    time: "8 min ago"
  }, {
    id: "#1236",
    table: "Counter",
    items: "3x Coffee, 2x Croissant",
    total: "$15.75",
    status: "completed",
    time: "12 min ago"
  }, {
    id: "#1237",
    table: "Table 8",
    items: "1x Burger, 1x Fries",
    total: "$16.99",
    status: "preparing",
    time: "15 min ago"
  }];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'preparing':
        return 'bg-warning text-warning-foreground';
      case 'ready':
        return 'bg-success text-success-foreground';
      case 'completed':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-primary text-primary-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Exact V1 Structure */}
      <header className="border-b border-border bg-card">
        <div className="px-6 py-4 mx-px">
          <div className="flex items-center justify-between mx-px my-[2px] px-[3px] py-[5px]">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <LayoutDashboard className="w-6 h-6 text-primary" />
                {restaurantName}
                <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">
                  Demo
                </Badge>
              </h1>
              <p className="text-muted-foreground">
                {activeSubdomain}.braisepos.com
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge className="bg-success/10 text-success border-success/20">
                <div className="w-2 h-2 bg-success rounded-full mr-2" />
                Online
              </Badge>
              <Button variant="outline" size="sm" onClick={() => handleNavigate('/table/1')}>
                View Menu
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-8">
        {/* Stats Cards - Exact V1 Structure */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {dashboardStats.map((stat, index) => (
            <Card key={index} className="card-elevated">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  <span className="text-success">
                    {stat.change}
                  </span>{" "}
                  from yesterday
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions - Exact V1 Structure */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Access the most important features of your restaurant
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {quickActions.map((action, index) => (
                <Button 
                  key={index} 
                  variant="ghost" 
                  className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-accent/50" 
                  onClick={() => handleNavigate(action.path)}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${action.color}`}>
                    <action.icon className="w-6 h-6" />
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-sm">{action.title}</div>
                    <div className="text-xs text-muted-foreground">{action.description}</div>
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders - Exact V1 Structure */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Recent Orders
                </CardTitle>
                <CardDescription>Latest orders from your restaurant</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleNavigate('/orders')}>
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentOrders.map((order, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{order.id}</span>
                        <Badge variant="outline" className="text-xs">
                          {order.table}
                        </Badge>
                        <Badge className={`text-xs ${getStatusColor(order.status)}`}>
                          {order.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{order.items}</p>
                      <p className="text-xs text-muted-foreground">{order.time}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{order.total}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick New Order - Exact V1 Structure */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                Quick Actions
              </CardTitle>
              <CardDescription>Common tasks and shortcuts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                className="w-full justify-start" 
                variant="default" 
                size="lg"
                onClick={handleNewCounterOrder}
              >
                <Plus className="w-5 h-5 mr-2" />
                New Counter Order
              </Button>
              
              <Button 
                className="w-full justify-start" 
                variant="secondary" 
                size="lg" 
                onClick={() => handleNavigate('/kitchen')}
              >
                <ChefHat className="w-5 h-5 mr-2" />
                Open Kitchen View
              </Button>
              
              <Button 
                className="w-full justify-start" 
                variant="outline" 
                size="lg" 
                onClick={() => handleNavigate('/qr-codes')}
              >
                <QrCode className="w-5 h-5 mr-2" />
                Generate QR Codes
              </Button>
              
              <Button 
                className="w-full justify-start" 
                variant="outline" 
                size="lg" 
                onClick={() => handleNavigate('/menu')}
              >
                <Menu className="w-5 h-5 mr-2" />
                Edit Menu
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Center Peek Modal for New Counter Order */}
      {isOrderModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transition-all duration-100 ${isModifying ? 'blur-sm scale-95' : ''}`}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">New Counter Order</h2>
                <p className="text-gray-600">Advanced order placement with customer details and modifications</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={closeOrderModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </Button>
            </div>

            {/* Customer Information Section */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input
                    type="text"
                    placeholder="Enter customer name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-1 min-h-0">
              {/* Menu Items Section */}
              <div className="flex-1 p-6 pb-8 overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Menu Items</h3>
                
                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search menu items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Category Filter */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {getAvailableCategories().map((category) => (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory(category)}
                        className="capitalize"
                      >
                        {category === 'all' ? 'All Items' : category}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Quick Actions - Popular Items */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Add Popular Items</h4>
                  <div className="flex flex-wrap gap-2">
                    {getPopularItems().map((item) => (
                      <Button
                        key={item.id}
                        variant="outline"
                        size="sm"
                        onClick={() => addItemToOrder(item)}
                        className="text-xs"
                      >
                        {item.name} (${item.price})
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Recent Orders for Quick Reorder */}
                {getRecentOrders().length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Reorder from Recent Orders</h4>
                    <div className="flex flex-wrap gap-2">
                      {getRecentOrders().map((order) => (
                        <Button
                          key={order.id}
                          variant="outline"
                          size="sm"
                          onClick={() => quickReorder(order)}
                          className="text-xs"
                        >
                          {order.order_code} - {order.customer_name ?? 'Customer'}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Menu Items Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {getFilteredMenuItems().map((item) => (
                    <div
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1 pr-2">{item.name}</h4>
                        <span className="text-lg font-bold text-primary flex-shrink-0">${item.price}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3 leading-relaxed">{item.description}</p>
                      
                      {/* Category Tag */}
                      <div className="mb-3">
                        <span className="inline-block text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full capitalize">
                          {item.category}
                        </span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            startModification(item);
                          }}
                          className="flex-1 h-8 text-xs font-medium border-gray-300 hover:border-primary hover:text-primary"
                        >
                          Modify
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            addItemToOrder(item);
                          }}
                          className="flex-1 h-8 text-xs font-medium border-gray-300 hover:border-primary hover:text-primary"
                        >
                          Add to Order
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* No Items Found Message */}
                {getFilteredMenuItems().length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-gray-500">
                      <Menu className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium mb-2">No items found</h3>
                      <p className="text-sm">
                        {searchQuery.trim() 
                          ? `No items match "${searchQuery}"`
                          : selectedCategory !== 'all' 
                            ? `No items in ${selectedCategory} category`
                            : 'No available items'
                        }
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('all');
                        }}
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Summary - World-Class UI/UX Design */}
              <div className="w-96 bg-gradient-to-br from-gray-50 to-white border-l border-gray-200 shadow-inner flex flex-col min-h-0">
                {/* Header */}
                <div className="p-6 border-b border-gray-200 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Order Summary</h3>
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary text-sm font-bold">{selectedItems.length}</span>
                    </div>
                  </div>
                  
                  {/* Order Notes */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Order Notes</label>
                    <textarea
                      placeholder="Special instructions for the order..."
                      value={orderNotes}
                      onChange={(e) => setOrderNotes(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-sm resize-none"
                      rows={2}
                    />
                  </div>

                </div>
                
                {selectedItems.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ShoppingBag className="w-10 h-10 text-gray-400" />
                      </div>
                      <p className="text-gray-500 font-medium mb-1">No items selected</p>
                      <p className="text-gray-400 text-sm">Add items to start your order</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Order Items */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                      {selectedItems.map((item) => (
                        <div key={item.item_id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 text-sm leading-tight">{item.item_name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                  Qty: {item.quantity}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ${item.price.toFixed(2)} each
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateItemQuantity(item.item_id, item.quantity - 1)}
                                className="w-7 h-7 p-0 text-gray-500 hover:text-gray-700"
                              >
                                -
                              </Button>
                              <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateItemQuantity(item.item_id, item.quantity + 1)}
                                className="w-7 h-7 p-0 text-gray-500 hover:text-gray-700"
                              >
                                +
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeItemFromOrder(item.item_id)}
                                className="w-7 h-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 ml-2"
                              >
                                ✕
                              </Button>
                            </div>
                          </div>
                          
                          {/* Modifications */}
                          {itemModifications[item.item_id] && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-orange-600 text-xs font-medium">Modifications</span>
                                <span className="text-orange-500 text-xs font-semibold">
                                  +${getModificationPrice(item.item_id, item.quantity).toFixed(2)}
                                </span>
                              </div>
                              <p className="text-orange-700 text-xs leading-relaxed">
                                {itemModifications[item.item_id]}
                              </p>
                            </div>
                          )}
                          
                          {/* Item Total */}
                          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                            <span className="text-xs text-gray-600">Item Total</span>
                            <span className="font-semibold text-gray-900">
                              ${getItemTotalPrice(item).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Order Totals */}
                    <div className="p-6 pb-8 border-t border-gray-200 bg-white">
                      <div className="bg-gray-50 rounded-xl p-4 mb-4">
                        <div className="space-y-3">
                          {/* Subtotal */}
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Subtotal</span>
                            <span className="text-gray-900 font-semibold">
                              ${selectedItems.reduce((sum, item) => sum + ((item.price ?? 0) * item.quantity), 0).toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Modifications Total */}
                          {Object.keys(itemModifications).length > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-orange-600 font-medium">Modifications</span>
                              <span className="text-orange-600 font-semibold">
                                +${selectedItems.reduce((sum, item) => sum + getModificationPrice(item.item_id, item.quantity), 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                          
                          {/* Divider */}
                          <div className="border-t border-gray-200 my-3"></div>
                          
                          {/* Grand Total */}
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-bold text-gray-900">Total</span>
                            <span className="text-2xl font-bold text-primary">${orderTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Checkout Button */}
                      <Button
                        className="w-full h-10 text-base font-semibold bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-200"
                        size="sm"
                        onClick={openCheckout}
                      >
                        <span className="mr-2">💳</span>
                        Proceed to Checkout
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Floating Place Order Button - Always Visible When Items Selected */}
          {selectedItems.length > 0 && (
            <div className="fixed bottom-6 right-6 z-40">
              <Button
                className="h-12 px-6 text-base font-semibold bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-2xl hover:shadow-3xl transition-all duration-200 rounded-full"
                size="sm"
                onClick={openCheckout}
              >
                <span className="mr-2">🛒</span>
                Place Order (${orderTotal.toFixed(2)})
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Animated Modification Overlay */}
      {isModifying && modifyingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] animate-in fade-in duration-100">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 animate-in zoom-in-95 duration-100">
            {/* Modification Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Customize {modifyingItem.name}</h2>
                <p className="text-gray-600">Add toppings and modifications to personalize this item</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={closeModification}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </Button>
            </div>

            {/* Item Preview */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🍕</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{modifyingItem.name}</h3>
                  <p className="text-gray-600">{modifyingItem.description}</p>
                  <p className="text-lg font-bold text-primary">${modifyingItem.price}</p>
                </div>
              </div>
            </div>

            {/* Available Toppings */}
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Available Toppings & Modifications</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {getAvailableToppings(modifyingItem).map((topping) => (
                  <Button
                    key={topping}
                    variant="outline"
                    size="sm"
                    onClick={() => addItemModification(modifyingItem.id, topping)}
                    className="h-auto p-3 flex flex-col items-center gap-1 hover:bg-primary hover:text-white transition-all duration-200"
                  >
                    <span className="text-sm font-medium">{topping}</span>
                  </Button>
                ))}
              </div>

              {/* Current Modifications */}
              {itemModifications[modifyingItem.id] && (
                <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-medium text-orange-800 mb-2">Current Modifications:</h4>
                  <p className="text-orange-700 text-sm">{itemModifications[modifyingItem.id]}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeItemModification(modifyingItem.id)}
                    className="mt-2 text-orange-600 border-orange-300 hover:bg-orange-100"
                  >
                    Clear All Modifications
                  </Button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button
                  className="flex-1"
                  size="lg"
                  onClick={() => {
                    addItemToOrder(modifyingItem);
                    closeModification();
                  }}
                >
                  Add to Order
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={closeModification}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Payment Modal */}
      {isCheckoutOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] animate-in fade-in duration-100 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-100">
            {/* Checkout Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Checkout</h2>
                <p className="text-gray-600">Select table and payment method to complete order</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={closeCheckout}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </Button>
            </div>

            {/* Order Summary */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold mb-4 text-gray-900">Order Summary</h3>
              <div className="space-y-3">
                {selectedItems.map((item) => (
                  <div key={item.item_id} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-sm">{item.quantity}x {item.item_name}</span>
                      <span className="font-semibold text-sm">${getItemTotalPrice(item).toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Base: ${((item.price ?? 0) * item.quantity).toFixed(2)}
                      {itemModifications[item.item_id] && (
                        <span className="text-orange-600 ml-2">
                          + Modifications: ${getModificationPrice(item.item_id, item.quantity).toFixed(2)}
                        </span>
                      )}
                    </div>
                    {itemModifications[item.item_id] && (
                      <div className="text-xs text-orange-600 mt-1 bg-orange-50 p-2 rounded">
                        {itemModifications[item.item_id]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">${selectedItems.reduce((sum, item) => sum + ((item.price ?? 0) * item.quantity), 0).toFixed(2)}</span>
                </div>
                {Object.keys(itemModifications).length > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-orange-600">Modifications:</span>
                    <span className="text-orange-600 font-semibold">
                      +${selectedItems.reduce((sum, item) => sum + getModificationPrice(item.item_id, item.quantity), 0).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-lg font-bold text-gray-900">Total:</span>
                  <span className="text-xl font-bold text-primary">${orderTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Table Selection */}
            <div className="p-6 border-b border-gray-200">
              <h3 className="font-semibold mb-3">Select Table</h3>
              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a table..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableTables().map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{table.name}</span>
                        <span className="text-xs text-gray-500">{table.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Method Selection */}
            <div className="p-6">
              <h3 className="font-semibold mb-3">Select Payment Method</h3>
              <div className="space-y-2">
                {/* Cash Payment */}
                <div 
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all duration-100 ${
                    selectedPaymentMethod === 'cash' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedPaymentMethod('cash')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">💵</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Cash</h4>
                      <p className="text-xs text-gray-600">Pay with cash</p>
                    </div>
                    {selectedPaymentMethod === 'cash' && (
                      <div className="ml-auto">
                        <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Payment */}
                <div 
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all duration-100 ${
                    selectedPaymentMethod === 'card' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedPaymentMethod('card')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">💳</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Card</h4>
                      <p className="text-xs text-gray-600">Credit or Debit Card</p>
                    </div>
                    {selectedPaymentMethod === 'card' && (
                      <div className="ml-auto">
                        <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button
                  className="flex-1"
                  size="lg"
                  onClick={processPayment}
                  disabled={!selectedPaymentMethod || !selectedTable}
                >
                  {selectedPaymentMethod === 'cash' ? 'Process Cash Payment' : 'Process Card Payment'}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={closeCheckout}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default V1DemoDashboard;
